/**
 * NewsletterSubscribe — footer opt-in card used across every public
 * page. POSTs to /api/newsletter/subscribe (see /app/backend/newsletter.py).
 * On duplicate submissions the backend returns 200 with
 * already_subscribed=true so we render a friendly note instead of an
 * error state.
 */
import { useState } from "react";
import axios from "axios";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterSubscribe({ source = "footer" }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  async function submit(evt) {
    evt.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setState({ status: "error", message: "Enter a valid email address." });
      return;
    }
    setState({ status: "loading", message: "" });
    try {
      const r = await axios.post(
        `${API}/api/newsletter/subscribe`,
        { email: trimmed, source },
        { headers: { "Content-Type": "application/json" } },
      );
      const already = !!r.data?.already_subscribed;
      setState({
        status: "success",
        message:
          r.data?.message ||
          (already
            ? "You're already on our list — we'll keep sending fresh guides."
            : "Subscribed. Check your inbox for a welcome note."),
      });
      if (!already) setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Something went wrong. Please try again.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg)
        msg = "Please enter a valid email address.";
      setState({ status: "error", message: msg });
    }
  }

  const isBusy = state.status === "loading";
  const isDone = state.status === "success";

  return (
    <section
      data-testid="footer-newsletter-card"
      aria-labelledby="footer-newsletter-heading"
      className="relative overflow-hidden rounded-md border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 sm:p-6"
    >
      <div className="pointer-events-none absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_15%_10%,rgba(56,189,248,0.22),transparent_55%),radial-gradient(circle_at_85%_90%,rgba(6,182,212,0.14),transparent_60%)]" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] text-brand-electric">
          <Mail className="h-3 w-3" /> Newsletter
        </div>
        <h4
          id="footer-newsletter-heading"
          className="mt-3 font-display font-black text-white text-lg leading-tight"
        >
          Fresh licensing guides,
          <br className="hidden sm:block" />
          straight to your inbox.
        </h4>
        <p className="mt-2 text-xs text-white/70 leading-relaxed">
          Buyer's guides on Microsoft 365, Autodesk, Adobe, Zoho and Windows Server —
          no spam, unsubscribe anytime.
        </p>
        <form
          onSubmit={submit}
          className="mt-4 flex flex-col sm:flex-row gap-2"
          noValidate
        >
          <label htmlFor="newsletter-email" className="sr-only">
            Email address
          </label>
          <input
            id="newsletter-email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(evt) => setEmail(evt.target.value)}
            disabled={isBusy || isDone}
            data-testid="newsletter-email-input"
            className="flex-1 rounded-sm bg-white/95 text-brand-navy placeholder:text-brand-navy/50 text-sm px-3 py-2.5 outline-none focus:ring-2 focus:ring-brand-electric transition-shadow disabled:opacity-70"
          />
          <button
            type="submit"
            disabled={isBusy || isDone}
            data-testid="newsletter-submit-button"
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-electric text-brand-navy text-xs font-black uppercase tracking-widest px-4 py-2.5 hover:bg-white transition-colors disabled:opacity-70"
          >
            {isBusy ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending
              </>
            ) : isDone ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" /> Subscribed
              </>
            ) : (
              "Subscribe"
            )}
          </button>
        </form>
        {state.message ? (
          <p
            role={state.status === "error" ? "alert" : "status"}
            data-testid={
              state.status === "error"
                ? "newsletter-error-message"
                : "newsletter-success-message"
            }
            className={
              "mt-3 text-xs leading-relaxed " +
              (state.status === "error"
                ? "text-rose-300"
                : "text-brand-electric")
            }
          >
            {state.message}
          </p>
        ) : null}
      </div>
    </section>
  );
}
